"use client";

import { usePathname } from "next/navigation";
import styles from "./BannerWithImage.module.scss";
import cn from "clsx";
import strapiSingleAttribute, { stripHtmlTags } from "@/api/strapiSingleAttribute";
import Markdown from "@/components/atoms/Markdown";
import CommonButton from "@/components/common/Analytics/CustomButton";
import { GAEvent, GA_EVENTS_PAYLOAD, IGAEvent, ga_events_mapping } from "@/helpers/gaEvents";
import { useEffect, useState } from "react";
import Image from "next/image";
import { StorageService } from "@/helpers/storage.service";
import { ModalTypes } from "@/types/modal";
import { removeMarkdown } from "@/helpers/markDown";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";

interface BannerWithImageProps {
  BannerData: BannerSlider;
  isMobile: boolean;
  fetchPriority?: "auto" | "high";
  specializationText: string;
  Custom10PgType?: string;

}

export default function BannerWithImage({
  BannerData,
  isMobile,
  fetchPriority = "auto",
  specializationText,
  Custom10PgType = ""
}: BannerWithImageProps) {
  const pathName = usePathname();
 
  const { PROGRAM_TAB_DOWNLOAD_BROCHURE, PROGRAM_CARD_DOWNLOAD_BROCHURE, PROGRAM_DOWNLOAD_BROCHURE } =
    GAEvent;
  const [gaData, setGaData] = useState<IGAEvent | null>(null);

  useEffect(() => {
    updateStateSession({
      formSectionName : "Download Brochure",
      sectionTitle : "Hero Banner",
      leadStartingCta : BannerData?.CTA?.text || "NA"
    });
  }, []);


  function getGaSessionData(): {
    isMbaPage?: boolean
    isNavbarClick?: boolean
  } | null {
    return StorageService.get("programDetailsGAInfo") ?? {}
  };
  useEffect(() => {
    try {
      const { isMbaPage = false, isNavbarClick } = getGaSessionData();
      if (ga_events_mapping?.[pathName] || isMbaPage) {
        let data: any
        if (isNavbarClick !== undefined) {
          data = {
            ...GA_EVENTS_PAYLOAD.get(
              isNavbarClick
                ? PROGRAM_TAB_DOWNLOAD_BROCHURE
                : PROGRAM_CARD_DOWNLOAD_BROCHURE
            ),
            pageType: isNavbarClick ? "Program Nav" : "Program Card",
          }
        } else {
          data = {
            ...GA_EVENTS_PAYLOAD.get(PROGRAM_DOWNLOAD_BROCHURE),
            pageType: "Program Details",
          }
        }
        setGaData(data);
      }
    } catch (error) {
      console.error("Error parsing programDetailsGAInfo from sessionStorage", error);
    }
  }, []);

  const image = strapiSingleAttribute(BannerData?.Image?.source);
  const mobileImage =
    strapiSingleAttribute(BannerData?.Image?.mobileSource) || image;
  const img = isMobile ? mobileImage : image;
  return (
    <div className="container">
      <div className={styles.BannerImage__container}>
        {/* Left section with box shadow for blue */}
        <div
          className={cn(styles.BannerImage__leftContainer, "text-primary-blue")}
        >
          {/* Upper subtitle and title section */}
          {/* Subtitle */}
          {BannerData?.ProgramType?.enabled ? (
            <p className={cn(styles.BannerImage__subtitle, "uppercase")}>
              {BannerData?.ProgramType?.text}
            </p>
          ) : null}
          {/* Title */}
          {BannerData?.Heading?.enabled ? (
            <Markdown as="h1" className={cn(styles.BannerImage__title)}>
             { BannerData?.Heading?.text.replace(/^#\s+(.*)$/gm, "$1")}
            </Markdown>
          ) : null}

          {/* Course duration */}
          {BannerData?.Duration?.enabled ? (
            <p className={cn(styles.BannerImage__duration, "uppercase")}>
              <span className={cn(styles.BannerImage__durationLabel)}>
                Duration:
              </span>{" "}
              {BannerData?.Duration?.text}
            </p>
          ) : null}
          {/* CTA */}
          {BannerData?.Sponsor?.enabled && (
                <div className={cn(styles.sponsor)}>
                  <p>{BannerData?.Sponsor?.Title}</p>
                  {BannerData?.Sponsor?.Image?.data && (
                    <img
                    src={BannerData?.Sponsor?.Image?.data?.attributes?.url}
                    alt={
                      BannerData?.Sponsor?.Image?.data?.attributes
                        ?.alternativeText
                        || ''
                    }
                    loading="eager"
                  />
                  )}
                </div>
              )}
          {BannerData?.CTA?.enabled && (
            <>
            <div className={cn(styles.BannerImage__cta)}>
              <CommonButton
                onClickStorePayload={
                  {
                    sectionTitle : "Hero Banner",
                    parentSection : "hero_banner"
                  }
                }
                GAData={gaData}
                GADataNew={{
                  event : "lead_initiate",
                  cta_text: BannerData?.CTA?.text,
                  section_name: "hero_banner",
                  header_title: removeMarkdown(BannerData?.Heading?.text) || "NA",
                  page_type: getGlobalStateValue('pageType'),
                  page_url : true
                }}
                CTATarget="_self"
                programTitle={BannerData?.Heading?.text}
                type={"button"}
                modalType={ModalTypes.DownloadBrochure}
                scroll={false}
                icon="/_a/icons/left-arrow-blue-down.svg"
                hoverIcon="/_a/icons/arrow-down-white.svg"
                bg="o"
                color="b"
                className={styles.BannerWithImageNew_CTAStyle}
                modalData={{ specializationText, Custom10PgType }}
              >
                {BannerData?.CTA?.text}
              </CommonButton>
              
              {BannerData?.Heading?.enabled && BannerData?.Heading?.noteText && (
                <div className={cn(styles.noteText)}>
                  <p>NOTE:- ONLY FOR STUDENTS RESIDING IN INDIA.</p>
                </div>
              )}
            </div>
            </>
          )}
        </div>

        {/* Right section with image */}
        {BannerData?.Image?.enabled ? (
          <div className={styles.img}>
            <Image
              src={img?.url}
              alt={BannerData?.Image?.alt || ''}
              className={cn(styles.BannerImage__image)}
              fill
              loading="eager"
              priority={!!(fetchPriority === "high")}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
